import { useAuthStore } from '@stores/auth.store';
import type { UserRole } from '@witzy/shared';
import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';


export interface ProtectedRouteProps {
  children: ReactNode;
  /** Optional role gate. If present, user's role must be in this list. */
  roles?: UserRole[];
  /** Override the default redirect destination. */
  fallback?: string;
  /**
   * Where to send an authenticated user whose role fails the gate.
   *
   * Defaults to the workspace, which is right when the signed-in account
   * simply has no business on the route. It is NOT right for routes that a
   * *different* account is meant to reach: bouncing to '/' renders AppLayout,
   * and a blocked wallet there throws up a full-screen overlay above the
   * TopBar — so a business user opening /admin landed on the blocker with no
   * visible way to sign in as an admin. Such routes pass '/login' instead, so
   * the answer to "wrong account" is the account switcher.
   */
  roleFallback?: string;
}

/**
 * Guards any authenticated route. Unauthenticated visitors go to /login and
 * the original path is preserved in `state.from` so login can bounce them
 * back after success.
 */
export function ProtectedRoute({
  children,
  roles,
  fallback = '/login',
  roleFallback = '/',
}: ProtectedRouteProps): JSX.Element {
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const impersonatedBy = useAuthStore((s) => s.impersonatedBy);

  if (!isAuthenticated || !user) {
    return <Navigate to={fallback} replace state={{ from: location.pathname }} />;
  }
  if (roles && !roles.includes(user.role)) {
    // A super-admin support session is signed in — as the customer. Sending it
    // to /login (the admin routes' roleFallback) dropped the admin on the login
    // page the moment Impersonate switched the session; send it into the app.
    const to = impersonatedBy ? '/' : roleFallback;
    // Carry the intended path so a successful sign-in lands where they aimed.
    return <Navigate to={to} replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}
